import { useEffect, useMemo, useContext, useState } from "react";
import { useParams } from "react-router-dom";
import { ErrorModalContext } from "../../../../components/AlertsModal/AlertsModal";
import { getSaleDetailService, refundSaleService, completeSaleService } from "../../../../services/sales";
import { getServiceError } from "../../../../utils/getServiceError";
import { SalesDetail, SaleStatus } from "../../../../interfaces/sales";
import { UseSaleReturn } from "../interfaces";

const SPEI_MERCHANTS = ['bankaool', 'clover'];

export const useSale = (): UseSaleReturn => {
  const { uuid } = useParams();
  const [sale, setSale] = useState<SalesDetail | null>(null);
  const { showAlert } = useContext(ErrorModalContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingRefund, setIsLoadingRefund] = useState<boolean>(false);
  const [isCompleting, setIsCompleting] = useState<boolean>(false);
  const [isConfirmingCash, setIsConfirmingCash] = useState<boolean>(false);
  const [showRefundModal, setShowRefundModal] = useState<boolean>(false);
  const [cancelPhoneNumber, setCancelPhoneNumber] = useState<string>('');
  const [cancelComment, setCancelComment] = useState<string>('');

  const getData = async (): Promise<void> => {
    try {
      setIsLoading(true);

      const response: SalesDetail = await getSaleDetailService(uuid || "");

      const formattedProducts = response.products.map((item) => {
        const parentProduct = item.uuid_parent
          ? response.products.find((p) => p.uuid === item.uuid_parent)?.product
          : null;

        return {
          ...item,
          discountName: item.uuid_parent ? "Complemento" : item.discountName,
          parentProduct,
        };
      });

      const sortedProducts = formattedProducts.sort((a, b) => {
        const getProductIndex = (product: typeof a): number =>
          formattedProducts.indexOf(product);

        const getParentIndex = (product: typeof a): number => {
          if (!product.uuid_parent) return -1;
          return formattedProducts.findIndex(
            (p) => p.uuid === product.uuid_parent,
          );
        };

        if (a.uuid_parent === b.uuid) return 1;
        if (b.uuid_parent === a.uuid) return -1;

        if (!a.uuid_parent && !b.uuid_parent)
          return getProductIndex(a) - getProductIndex(b);

        if (!a.uuid_parent && b.uuid_parent) {
          const bParentIndex = getParentIndex(b);
          return getProductIndex(a) - bParentIndex;
        }

        if (!b.uuid_parent && a.uuid_parent) {
          const aParentIndex = getParentIndex(a);
          return aParentIndex - getProductIndex(b);
        }

        if (a.uuid_parent && b.uuid_parent) {
          if (a.uuid_parent === b.uuid_parent)
            return getProductIndex(a) - getProductIndex(b);

          return getParentIndex(a) - getParentIndex(b);
        }

        return 0;
      });

      setSale({ ...response, products: sortedProducts });
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      const msg = getServiceError(error);
      showAlert(msg, "error");
    }
  };

  const cantRefund: string[] = useMemo(() => {
    const cantRefund: string[] = [];
    sale?.payments.forEach((item) => {
      if (item.status === 0) {
        cantRefund.push(item.code);
      }
    });

    return cantRefund;
  }, [sale]);

  const requiresSpei: boolean = useMemo(() => {
    return (sale?.payments ?? []).some(
      (p) => p.card_transaction && SPEI_MERCHANTS.includes(p.card_transaction.merchant?.toLowerCase() ?? '')
    );
  }, [sale]);

  const cardRefundTotal: number = useMemo(() => {
    return (sale?.payments ?? [])
      .filter((p) => p.card_transaction && SPEI_MERCHANTS.includes(p.card_transaction.merchant?.toLowerCase() ?? ''))
      .reduce((sum, p) => sum + (p.subtotal ?? 0), 0);
  }, [sale]);

  /** Whether the sale has at least one cash payment. */
  const hasCashPayment: boolean = useMemo(() => {
    return (sale?.payments ?? []).some((p) => !!p.cash_transaction);
  }, [sale]);

  const makeRefund = async (): Promise<void> => {
    try {
      if (cantRefund.length > 0) {
        let codes = "";
        cantRefund.forEach((item) => {
          codes = `${codes}, ${item}`;
        });
        setShowRefundModal(false);
        return showAlert(
          `Las ventas con los códigos: ${codes} no están disponibles para reembolso, debido a su estado`,
          "error",
        );
      }
      setIsLoadingRefund(true);
      await refundSaleService(
        sale?.uuid || "",
        sale?.payments[0]?.card_transaction?.transaction_id || "",
        requiresSpei ? cancelPhoneNumber : undefined,
        cancelComment,
      );
      setIsLoadingRefund(false);
      setShowRefundModal(false);
      showAlert(
        `La venta ${sale?.code} ha sido cancelada`,
        "info",
        "Venta cancelada",
      );
      getData();
    } catch (error) {
      setIsLoadingRefund(false);
      const msg = getServiceError(error);
      showAlert(msg, "error");
    }
  };

  const completeSale = async (options?: { tip?: number; paymentType?: "card" | "cash" }): Promise<void> => {
    try {
      setIsCompleting(true);
      await completeSaleService(sale?.uuid || "", {
        tip: options?.tip,
        payment_type: options?.paymentType,
      });
      setIsCompleting(false);
      showAlert(`La venta ${sale?.code} ha sido marcada como pagada`, "success", "Venta completada");
      getData();
    } catch (error) {
      setIsCompleting(false);
      const msg = getServiceError(error);
      showAlert(msg, "error");
    }
  };

  /**
   * Confirms a cash payment for sales in "Por confirmar" status.
   * Reuses the complete sale endpoint with payment_type "cash".
   */
  const confirmCashPayment = async (): Promise<void> => {
    try {
      setIsConfirmingCash(true);
      await completeSaleService(sale?.uuid || "", { payment_type: "cash" });
      setIsConfirmingCash(false);
      showAlert(
        `El pago en efectivo de la venta ${sale?.code} ha sido confirmado`,
        "success",
        "Pago confirmado",
      );
      getData();
    } catch (error) {
      setIsConfirmingCash(false);
      const msg = getServiceError(error);
      showAlert(msg, "error");
    }
  };

  useEffect(() => {
    getData();
  }, [uuid]);

  return {
    cantRefund,
    isLoading,
    isLoadingRefund,
    isCompleting,
    isConfirmingCash,
    hasCashPayment,
    makeRefund,
    completeSale,
    confirmCashPayment,
    refreshData: getData,
    sale,
    setShowRefundModal,
    showRefundModal,
    requiresSpei,
    cancelPhoneNumber,
    setCancelPhoneNumber,
    cancelComment,
    setCancelComment,
    cardRefundTotal,
  };
};
